/* The e-receipts module: the register, the editor and the detail. Our own layout, RTL first. */

.subtle {
  color: var(--color-text-subtle);
  font-size: var(--text-sm);
}

.mono {
  font-family: var(--font-mono);
  font-size: var(--text-sm);
  overflow-wrap: anywhere;
}

.end {
  text-align: end;
}

/* ------------------------------------------------------------------ hero */
.hero {
  background: var(--gradient-hero);
  color: var(--color-on-hero);
  border-radius: var(--radius-lg);
  padding: var(--space-6);
  box-shadow: var(--shadow-md);
}

.heroRow {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  align-items: flex-start;
  justify-content: space-between;
}

.heroText {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-inline-size: 0;
}

.heroEyebrow {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
  color: color-mix(in srgb, var(--color-on-hero) 82%, transparent);
}

.heroTitle {
  margin: 0;
  font-size: var(--text-2xl);
  font-weight: var(--weight-bold);
  line-height: var(--leading-tight);
}

.heroSub {
  margin: 0;
  font-size: var(--text-sm);
  color: color-mix(in srgb, var(--color-on-hero) 85%, transparent);
}

.heroActions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: center;
}

.headerBadges {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-block-start: var(--space-2);
}

.typeBadge {
  background: color-mix(in srgb, var(--color-on-hero) 18%, transparent);
  color: var(--color-on-hero);
}

.live {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  margin-block-start: var(--space-3);
  font-size: var(--text-sm);
}

.dot {
  inline-size: 0.5rem;
  block-size: 0.5rem;
  border-radius: var(--radius-pill);
  background: var(--color-on-hero);
  animation: pulse 1.2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}

/* ------------------------------------------------------------------ find panel */
.findPanel {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  align-items: flex-start;
  padding: var(--space-4);
  margin-block: var(--space-4);
  background: var(--color-surface);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
}

.findSearch {
  flex: 1 1 16rem;
  min-inline-size: 12rem;
}

.findHeading {
  margin: 0 0 var(--space-1);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}

.search {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding-inline: var(--space-3);
  block-size: var(--control-height);
  border: var(--border-width) solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text-muted);
}

.search input {
  flex: 1;
  border: 0;
  background: transparent;
  color: var(--color-text);
  font-size: var(--text-md);
}

.search input:focus {
  outline: none;
}

.findHint {
  margin: var(--space-1) 0 0;
  font-size: var(--text-xs);
  color: var(--color-text-subtle);
}

.facetsDisclosure {
  flex: 1 1 20rem;
}

.facetsTrigger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  border: 0;
  background: transparent;
  color: var(--color-heading);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  cursor: pointer;
}

.facetsChevron {
  transition: transform var(--duration-fast) var(--ease-out);
}

.facetsTrigger[aria-expanded='true'] .facetsChevron {
  transform: rotate(180deg);
}

.facets {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-5);
  align-items: flex-start;
  padding-block-start: var(--space-3);
}

.facetChecks {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-inline-size: 9rem;
}

.clearFacets {
  align-self: flex-end;
}

/* ------------------------------------------------------------------ table */
.numberLink {
  font-family: var(--font-mono);
  font-weight: var(--weight-semibold);
  color: var(--color-link);
}

.sortButton {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: var(--weight-semibold);
  cursor: pointer;
}

.axisCell {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: center;
}

/* Beside an Invalid chip: the receipt was refused at submission (RB3R8-04). */
.axisNote {
  flex-basis: 100%;
  font-size: var(--text-xs);
  color: var(--color-text-subtle);
}

/* The receipt cell: the number, and the kind as a second, quieter line. */
.receiptCell {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-inline-size: 0;
}

.typeLine {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.updatedCell {
  font-size: var(--text-sm);
}

/* ------------------------------------------------------------------ cards */
.card {
  background: var(--color-surface);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
  margin-block: var(--space-4);
}

.cardTitle {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0 0 var(--space-3);
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}

.cardIcon {
  display: inline-flex;
  color: var(--color-text-muted);
}

.formGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  gap: var(--space-4);
}

/* A value the user does not choose here: the device's branch, a saved receipt's type (WP-8.9c). */
.readField {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-inline-size: 0;
}

.readLabel {
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--color-text);
}

.readValue {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-2);
  min-block-size: var(--control-height);
  padding-block: var(--space-2);
}

.readMeta,
.readHint {
  font-size: var(--text-xs);
  color: var(--color-text-subtle);
}

/* A server refusal of a value the user does not type (the branch follows the device). */
.readError {
  font-size: var(--text-xs);
  color: var(--color-danger-text);
}

.noDevice {
  margin: 0 0 var(--space-3);
  padding: var(--space-3);
  border-inline-start: 3px solid var(--color-warning-border);
  background: var(--color-warning-bg);
  color: var(--color-warning-text);
}

.backLine {
  margin-block: var(--space-4);
}

.issueField {
  font-weight: var(--weight-semibold);
}

/* Amounts and quantities never break inside their digits (RB3R8-09). */
.num {
  white-space: nowrap;
  unicode-bidi: isolate;
  font-variant-numeric: tabular-nums;
}

.reasonLead {
  margin: 0;
}

.reasonList {
  margin: var(--space-2) 0 0;
  padding-inline-start: var(--space-5);
}

.techDetails {
  margin-block-start: var(--space-3);
  font-size: var(--text-sm);
}

.techDetails summary {
  cursor: pointer;
  color: var(--color-link);
}

.techBody {
  margin: var(--space-2) 0 0;
  padding: var(--space-3);
  border: var(--border-width) solid var(--color-rule);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken, var(--color-bg));
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.nextStep {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  margin-block-start: var(--space-3);
}

/* ------------------------------------------------------------------ key/value */
.kv {
  display: grid;
  grid-template-columns: minmax(8rem, max-content) 1fr;
  gap: var(--space-2) var(--space-4);
  margin: 0;
}

.kv dt {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.kv dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.totalTerm {
  font-weight: var(--weight-semibold);
}

.kvTable {
  inline-size: 100%;
  border-collapse: collapse;
}

.kvTable th,
.kvTable td {
  padding: var(--space-2);
  border-block-end: var(--border-width) solid var(--color-rule);
  text-align: start;
}

/* ------------------------------------------------------------------ lines */
.lineTable {
  inline-size: 100%;
  border-collapse: collapse;
}

.lineTable th,
.lineTable td {
  padding: var(--space-2);
  border-block-end: var(--border-width) solid var(--color-rule);
  text-align: start;
  vertical-align: top;
}

.lineTable thead th {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.lineName {
  font-weight: var(--weight-medium);
}

/* The name of a line's quantity or price, shown only where the header row is hidden (phones). */
.lineLabel {
  display: none;
}

/* A line's taxes under its product (DEC-111): a rate tax as text, a fixed fee as an amount field. */
.lineTaxes {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin-block-start: var(--space-2);
}

.lineTaxesLabel {
  font-size: var(--text-xs);
  color: var(--color-text-subtle);
}

.lineTaxList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.lineTaxRate {
  font-size: var(--text-sm);
}

.lineActions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  align-items: center;
  margin-block-start: var(--space-3);
}

.totalsNote {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--color-text-subtle);
}

.optionTitle {
  display: block;
  font-weight: var(--weight-medium);
}

.optionMeta {
  display: block;
  font-size: var(--text-xs);
  color: var(--color-text-subtle);
}

/* ------------------------------------------------------------------ actions and blockers */
.editorActions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-block: var(--space-5);
}

.blockers {
  margin-block: var(--space-4);
}

.blockerList {
  margin: 0;
  padding-inline-start: var(--space-5);
}

.issuePath {
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.textButton {
  display: inline-flex;
  align-items: center;
  color: var(--color-on-hero);
  font-weight: var(--weight-medium);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.primaryButton {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  border: 0;
  border-radius: var(--radius-md);
  padding: var(--space-2) var(--space-4);
  background: var(--color-surface);
  color: var(--color-primary);
  font-weight: var(--weight-semibold);
  cursor: pointer;
}

.primaryButton:disabled {
  opacity: 0.6;
  cursor: progress;
}

/* ------------------------------------------------------------------ history */
.timeline {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.timeline li {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  align-items: baseline;
  padding-block-end: var(--space-2);
  border-block-end: var(--border-width) solid var(--color-rule);
}

.timelineAt {
  font-variant-numeric: tabular-nums;
  font-size: var(--text-xs);
  color: var(--color-text-subtle);
  min-inline-size: 10rem;
}

.timelineEvent {
  font-weight: var(--weight-medium);
}

@media (max-width: 40rem) {
  .kv {
    grid-template-columns: 1fr;
  }
  /* The editor's lines stack (WP-8.9): five input columns do not fit 390 px, and squeezed they
     showed one character of the item and of the price. Item on its own row, then quantity, price
     and remove, each named; the page never scrolls sideways. */
  .editLines thead {
    position: absolute;
    inline-size: 1px;
    block-size: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
  .editLines,
  .editLines tbody {
    display: block;
  }
  .editLines tbody tr {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) minmax(0, 1fr) auto;
    grid-template-areas:
      'no item item item'
      '. qty price remove';
    gap: var(--space-2);
    align-items: end;
    padding-block: var(--space-3);
    border-block-end: var(--border-width) solid var(--color-rule);
  }
  .editLines td {
    display: block;
    padding: 0;
    border: 0;
    text-align: start;
  }
  .editLines td:nth-child(1) {
    grid-area: no;
    align-self: center;
  }
  .editLines td:nth-child(2) {
    grid-area: item;
  }
  .editLines td:nth-child(3) {
    grid-area: qty;
  }
  .editLines td:nth-child(4) {
    grid-area: price;
  }
  .editLines td:nth-child(5) {
    grid-area: remove;
  }
  .lineLabel {
    display: block;
    margin-block-end: var(--space-1);
    font-size: var(--text-xs);
    color: var(--color-text-muted);
  }
  /* The receipt page's lines stack the same way (RB3R8-09): the product on its own row, then each
     value with its name, so no number is squeezed into a narrow column. */
  .viewLines thead {
    position: absolute;
    inline-size: 1px;
    block-size: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
  .viewLines,
  .viewLines tbody {
    display: block;
  }
  .viewLines tbody tr {
    display: grid;
    grid-template-columns: auto repeat(2, minmax(0, 1fr));
    gap: var(--space-1) var(--space-3);
    padding-block: var(--space-3);
    border-block-end: var(--border-width) solid var(--color-rule);
  }
  .viewLines td {
    display: block;
    padding: 0;
    border: 0;
    text-align: start;
  }
  .viewLines td:nth-child(1) {
    grid-row: 1;
    grid-column: 1;
  }
  .viewLines td:nth-child(2) {
    grid-row: 1;
    grid-column: 2 / -1;
  }
  .viewLines td:nth-child(3) {
    grid-column: 2;
  }
  .viewLines td:nth-child(4) {
    grid-column: 3;
  }
  .viewLines td:nth-child(5) {
    grid-column: 2 / -1;
  }
  .editorActions {
    position: sticky;
    inset-block-end: 0;
    background: var(--color-bg);
    padding-block: var(--space-3);
  }
}
